// Extract plain text from an uploaded resume file.
// Text-based PDFs: parsed client-side via pdfjs-dist.
// Image-based PDFs: pages rendered to images, sent to /api/ocr for AI-powered OCR.
// Also extracts candidate photo if present (cropped to 512x512).

import * as pdfjsLib from "pdfjs-dist";

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

export interface ExtractionResult {
  text: string;
  photo: string | null; // base64 data-URL of 512x512 JPEG, or null
}

export async function extractTextFromFile(
  file: File,
  onStatus?: (status: string) => void
): Promise<ExtractionResult> {
  const name = file.name.toLowerCase();

  if (name.endsWith(".pdf") || file.type === "application/pdf") {
    return extractPdf(file, onStatus);
  }
  if (
    name.endsWith(".txt") ||
    name.endsWith(".md") ||
    file.type.startsWith("text/")
  ) {
    return { text: await file.text(), photo: null };
  }
  throw new Error(
    `Unsupported file type: ${file.type || name}. Upload a PDF or plain-text file.`
  );
}

async function extractPdf(
  file: File,
  onStatus?: (status: string) => void
): Promise<ExtractionResult> {
  const buf = await file.arrayBuffer();
  if (buf.byteLength === 0) {
    throw new Error("PDF file is empty (0 bytes).");
  }

  onStatus?.("Parsing PDF…");
  const data = new Uint8Array(buf);

  let doc: pdfjsLib.PDFDocumentProxy;
  try {
    doc = await pdfjsLib.getDocument({
      data: data.slice(0),
      useWorkerFetch: false,
      isEvalSupported: false,
      useSystemFonts: true,
    }).promise;
  } catch (e) {
    throw new Error(
      `Failed to parse PDF: ${e instanceof Error ? e.message : String(e)}`
    );
  }

  if (doc.numPages === 0) {
    throw new Error("PDF has no pages.");
  }

  // Render first page for photo extraction (and possibly OCR)
  onStatus?.("Rendering first page…");
  const firstPage = await renderPageWithDimensions(doc, 1);

  // Start photo extraction in parallel with text extraction
  const photoPromise = firstPage
    ? extractPhoto(firstPage.dataUrl, firstPage.width, firstPage.height, onStatus)
    : Promise.resolve(null);

  // Step 1: Try text extraction
  onStatus?.("Extracting text…");
  let textResult = await extractTextFromPages(doc);

  if (!textResult || textResult.length < 50) {
    // Step 2: Fall back to OCR
    onStatus?.("Image-based PDF detected. Running OCR…");
    const images = await renderPagesToImages(doc);
    if (images.length === 0) {
      throw new Error("Could not render PDF pages to images.");
    }
    onStatus?.(`Sending ${images.length} page(s) to AI for text recognition…`);
    const ocrText = await callOcrApi(images);
    if (!ocrText || ocrText.length < 20) {
      throw new Error(
        "OCR could not extract enough text from this PDF. Please try a clearer document."
      );
    }
    textResult = ocrText;
  }

  const photo = await photoPromise;

  return { text: textResult, photo };
}

async function extractPhoto(
  pageImage: string,
  pageWidth: number,
  pageHeight: number,
  onStatus?: (status: string) => void
): Promise<string | null> {
  try {
    onStatus?.("Detecting candidate photo…");
    const res = await fetch("/api/extract-photo", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        image: pageImage,
        page_width: pageWidth,
        page_height: pageHeight,
      }),
    });

    if (!res.ok) return null;
    const box = (await res.json()) as {
      found: boolean;
      x: number;
      y: number;
      w: number;
      h: number;
    };
    if (!box.found) return null;

    onStatus?.("Cropping candidate photo…");
    return await cropPhoto(pageImage, box);
  } catch {
    return null;
  }
}

async function cropPhoto(
  pageImage: string,
  box: { x: number; y: number; w: number; h: number }
): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      // Pixel coordinates from Claude — clamp to image bounds
      let sx = Math.max(0, box.x);
      let sy = Math.max(0, box.y);
      let sw = Math.min(box.w, img.width - sx);
      let sh = Math.min(box.h, img.height - sy);

      // Sanity check
      if (sw < 10 || sh < 10) {
        resolve(null);
        return;
      }

      // Make it square — center-crop the longer side
      const side = Math.min(sw, sh);
      const cx = sx + (sw - side) / 2;
      const cy = sy + (sh - side) / 2;

      const canvas = document.createElement("canvas");
      canvas.width = 512;
      canvas.height = 512;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(null);
        return;
      }

      ctx.drawImage(img, cx, cy, side, side, 0, 0, 512, 512);

      // Verify the crop isn't blank (all white/transparent)
      const sample = ctx.getImageData(128, 128, 256, 256).data;
      let nonWhitePixels = 0;
      for (let i = 0; i < sample.length; i += 4) {
        if (sample[i] < 240 || sample[i + 1] < 240 || sample[i + 2] < 240) {
          nonWhitePixels++;
        }
      }
      // If less than 5% of sampled pixels are non-white, it's blank
      if (nonWhitePixels < (256 * 256) * 0.05) {
        resolve(null);
        return;
      }

      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = () => resolve(null);
    img.src = pageImage;
  });
}

interface PageRender {
  dataUrl: string;
  width: number;
  height: number;
}

async function renderPageWithDimensions(
  doc: pdfjsLib.PDFDocumentProxy,
  pageNum: number
): Promise<PageRender | null> {
  try {
    const page = await doc.getPage(pageNum);
    const scale = 1.5;
    const MAX_DIM = 1600;
    let viewport = page.getViewport({ scale });

    if (viewport.width > MAX_DIM || viewport.height > MAX_DIM) {
      const ratio = Math.min(MAX_DIM / viewport.width, MAX_DIM / viewport.height);
      viewport = page.getViewport({ scale: scale * ratio });
    }

    const canvas = document.createElement("canvas");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    await page.render({ canvasContext: ctx, viewport }).promise;
    return {
      dataUrl: canvas.toDataURL("image/jpeg", 0.7),
      width: canvas.width,
      height: canvas.height,
    };
  } catch {
    return null;
  }
}

async function renderPageToImage(
  doc: pdfjsLib.PDFDocumentProxy,
  pageNum: number
): Promise<string | null> {
  const result = await renderPageWithDimensions(doc, pageNum);
  return result?.dataUrl ?? null;
}

async function extractTextFromPages(
  doc: pdfjsLib.PDFDocumentProxy
): Promise<string> {
  const parts: string[] = [];

  for (let i = 1; i <= doc.numPages; i++) {
    try {
      const page = await doc.getPage(i);
      const content = await page.getTextContent();
      const lines: string[] = [];
      let lastY: number | null = null;

      for (const item of content.items) {
        if (!("str" in item) || typeof item.str !== "string") continue;
        if (!item.str) continue;

        const y = "transform" in item ? (item as any).transform[5] : null;
        if (lastY !== null && y !== null && Math.abs(y - lastY) > 2) {
          lines.push("\n");
        }
        lastY = y;
        lines.push(item.str);
      }

      const pageText = lines.join(" ").replace(/\n /g, "\n").trim();
      if (pageText) {
        parts.push(pageText);
      }
    } catch {
      // Skip pages that fail
    }
  }

  return parts
    .join("\n\n")
    .replace(/[ \t]+/g, " ")
    .replace(/ \n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

async function renderPagesToImages(
  doc: pdfjsLib.PDFDocumentProxy
): Promise<string[]> {
  const images: string[] = [];
  const maxPages = Math.min(doc.numPages, 10);

  for (let i = 1; i <= maxPages; i++) {
    const img = await renderPageToImage(doc, i);
    if (img) images.push(img);
  }

  return images;
}

async function callOcrApi(images: string[]): Promise<string> {
  const res = await fetch("/api/ocr", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ images }),
  });

  if (!res.ok) {
    let detail = "";
    try {
      const err = await res.json();
      detail = (err as any).error || JSON.stringify(err);
    } catch {
      detail = await res.text();
    }
    throw new Error(`OCR failed: ${detail}`);
  }

  const data = (await res.json()) as { text: string };
  return data.text;
}
